import { MediaType } from 'types/AbstractMedia';
import {
  BasicContract, SelfService, SelfServiceOrigin, SelfServiceType, SignatureStatus,
} from 'modules/selfServices/types/SelfService';
import { DocumentKeys, DocumentOptions, SignDocument } from './types';

const formatContract = ({
  id, name, status, media,
}: BasicContract, options: DocumentOptions) => ({
  id,
  status,
  filename: options?.getLabelTranslation?.(name),
  read: status === SignatureStatus.SIGNED,
  type: media?.type,
  path: media?.path,
});

const getCheckInDocuments = (selfService: SelfService, options: DocumentOptions): SignDocument[] => [
  // Breakdown always displays summary + signature step
  ...(selfService?.type === SelfServiceType.BREAKDOWN || selfService?.dealer?.summarySignatureEnabled
    ? [
      {
        read: false,
        path: undefined,
        type: MediaType.DOCUMENT,
        id: DocumentKeys.SUMMARY,
        get: options?.retrieveSummaryDocument,
        status: SignatureStatus.UNSIGNED,
        filename: options?.intl?.formatMessage({
          id: 'signature.documents.SUMMARY',
          defaultMessage: 'Check-in summary',
        }),
      } as SignDocument,
    ]
    : []),
  ...(selfService.workOrder?.media
    ? [
      {
        type: MediaType.DOCUMENT,
        id: DocumentKeys.WORK_ORDER,
        status: selfService?.workOrder.status,
        path: selfService.workOrder.media.path,
        read: selfService.workOrder.status === SignatureStatus.SIGNED,
        filename: options?.intl?.formatMessage({
          id: 'signature.documents.WORK_ORDER',
          defaultMessage: 'Work order',
        }),
      },
    ]
    : []),
  ...(options?.hasAcceptedMobility && selfService.mobility?.contract?.media
    ? [
      {
        type: MediaType.DOCUMENT,
        id: DocumentKeys.MOBILITY,
        status: selfService.mobility.contract.status,
        path: selfService.mobility.contract.media.path,
        read: selfService.mobility.contract.status === SignatureStatus.SIGNED,
        filename: options?.intl?.formatMessage({
          id: 'mobilityContract.title',
          defaultMessage: 'Mobility contract',
        }),
      },
    ]
    : []),
];

const getCheckOutDocuments = (selfService: SelfService, options: DocumentOptions): SignDocument[] => [
  ...(selfService.origin !== SelfServiceOrigin.HOME && selfService.mobilityEnabled && selfService?.mobility?.deliveryDocument?.media
    ? [
      {
        type: MediaType.DOCUMENT,
        id: DocumentKeys.DELIVERY_DOCUMENT,
        status: selfService.mobility.deliveryDocument.status,
        path: selfService.mobility.deliveryDocument.media.path,
        read: selfService.mobility.deliveryDocument.status === SignatureStatus.SIGNED,
        filename: options?.intl?.formatMessage({
          id: 'signature.documents.DELIVERY_DOCUMENT',
          defaultMessage: 'Vehicle delivery document',
        }),
      },
    ]
    : []),
];

export const getDocuments = (selfService: SelfService, options: DocumentOptions) => [
  ...(selfService.type === SelfServiceType.CHECK_OUT
    ? getCheckOutDocuments(selfService, options)
    : getCheckInDocuments(selfService, options)),
  ...(selfService.documents?.map((document) => formatContract(document, options)) ?? []),
];

export const hasDocumentsToSign = (selfService: SelfService, options?: DocumentOptions) => getDocuments(selfService, options).some(
  (document) => document.id !== DocumentKeys.SUMMARY && document.status === SignatureStatus.UNSIGNED,
);
